import moment from 'moment';
import {useCallback, useEffect, useState} from 'react';
import Util from 'src/Util';
import {HistoryComponentProps} from './../HistoryComponent';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import AccountStore from 'src/stores/AccountStore';
import AccountConstants from 'src/constants/AccountConstants';
import ActionsFactory from 'src/actions/ActionsFactory';
import uuid from 'src/nativeModules/UUID';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import Events from 'src/logging/Events';

export const useHistory = (
  props: HistoryComponentProps,
  page: number,
  fundingPage: number,
  accountId: string
) => {
  const [started, setStarted] = useState(moment());
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [history, setHistory] = useState(
    AccountStore.filteredAndSortedHistory(props.historyfilter),
  );

  useEffect(() => {
    setIsLoading(true);

    FirebaseAnalytic.trackEvent('useEffect', 'HistoryComponent', {
      ...props,
    });

    fetchHistory(props.historyfilter);

    AccountStore.addHistoryChangedListener(onChange);

    return () => {
      AccountStore.removeHistoryChangedListener(onChange);
    };
  }, [props.historyfilter]);

  const fetchHistory = useCallback(
    async (filter: AccountConstants) => {
      try {
        if (filter === AccountConstants.FUNDING_TYPE) {
          await ActionsFactory.getAccountActions().loadPurchaseHistory(
            accountId,
            fundingPage,
            AccountConstants.FUNDING_TYPE,
          );
        } else if (filter === AccountConstants.REDEMPTION_TYPE) {
          await ActionsFactory.getAccountActions().loadPurchaseHistory(
            accountId,
            fundingPage,
            AccountConstants.REDEMPTION_TYPE,
          );
        } else if (filter === AccountConstants.SEND_SNACK) {
          // Create an array of promises
          const promises = [
            ActionsFactory.getAccountActions().loadPurchaseHistory(
              accountId,
              fundingPage,
              AccountConstants.SEND_SNACK,
            ),
            ActionsFactory.getAccountActions().loadPurchaseHistory(
              accountId,
              fundingPage,
              AccountConstants.SEND_SNACK_CANCEL,
            ),
            ActionsFactory.getAccountActions().loadPurchaseHistory(
              accountId,
              fundingPage,
              AccountConstants.SEND_SNACK_REVERSE,
            ),
            ActionsFactory.getAccountActions().loadPurchaseHistory(
              accountId,
              fundingPage,
              AccountConstants.SEND_SNACK_ACCEPT,
            ),
            ActionsFactory.getAccountActions().loadPurchaseHistory(
              accountId,
              fundingPage,
              AccountConstants.SEND_SNACK_EXPIRE,
            ),
          ];

          // Use Promise.all to execute all promises concurrently
          await Promise.all(promises);
        } else if (filter === AccountConstants.SALE_TYPE) {
          await ActionsFactory.getAccountActions().loadPurchaseHistory(
            accountId,
            fundingPage,
            AccountConstants.SALE_TYPE,
          );
        } else if (filter === AccountConstants.MARKET_PURCHASES) {
          await ActionsFactory.getAccountActions().loadPurchaseHistory(
            accountId,
            fundingPage,
            AccountConstants.SALE_TYPE,
            AccountConstants.MARKET_PURCHASES,
          );
        } else if (filter === AccountConstants.MOBILE_PURCHASES) {
          await ActionsFactory.getAccountActions().loadPurchaseHistory(
            accountId,
            fundingPage,
            AccountConstants.SALE_TYPE,
            AccountConstants.MOBILE_PURCHASES,
          );
        } else if (filter === AccountConstants.REFERRAL_TYPE) {
          await ActionsFactory.getAccountActions().loadPurchaseHistory(
            accountId,
            fundingPage,
            AccountConstants.REFERRAL_TYPE,
          );
        } else if (
          filter === AccountConstants.SIGN_UP_REWARDS ||
          filter === AccountConstants.REFERRAL_SIGNUP_TYPE
        ) {
          await ActionsFactory.getAccountActions().loadPurchaseHistory(
            accountId,
            fundingPage,
            AccountConstants.REFERRAL_SIGNUP_TYPE,
          );
        } else if (filter === AccountConstants.ADJUSTMENT_TYPE) {
          await ActionsFactory.getAccountActions().loadPurchaseHistory(
            accountId,
            fundingPage,
            AccountConstants.ADJUSTMENT_TYPE,
          );
        } else {
          await ActionsFactory.getAccountActions().loadPurchaseHistory(
            accountId,
            page,
          );
        }
      } catch (error) {
        const guid = await uuid.getRandomUUID();
        CrashlyticsEvents.log(
          'Exception',
          'HistoryComponent:onRefresh',
          generateErrorMessage(error),
          guid,
        );
        Events.Error.trackEvent(
          'Exception',
          'HistoryComponent:onRefresh',
          generateErrorMessage(error),
          guid,
        );
      }
    },
    [props.historyfilter],
  );

  const onChange = async () => {
    setHistory(AccountStore.filteredAndSortedHistory(props.historyfilter));
    await Util.delayForUX(started);
    setTimeout(() => {
      setIsRefreshing(false);
      setIsLoading(false);
    }, 4000);
  };

  return {
    isLoading,
    isRefreshing,
    setIsRefreshing,
    history,
    setStarted,
  };
};
